import { NotificationCenterModule } from '@pure/notification-center';
import { Angulartics2RouterlessModule } from 'angulartics2';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, DOCUMENT, ErrorHandler, importProvidersFrom, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import MarkerClusterer from '@google/markerclustererplus';
import { NgbCarouselConfig, NgbConfig, NgbModalConfig, NgbModule, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { AuthorizationService, AuthorizerModule } from '@pure/authz-authorizer';
import { HIVE_THEMING, SVG_ROOT, Themes } from '@pure/hive';
import {
    CachedCurrentUserService,
    PlatformClientService,
    Pure1UIPlatformModule,
} from '@pure/pure1-ui-platform-angular';
import { DefaultResourceService, FeatureFlagDxpService } from '@pure1/data';

import { EffectiveOrgService } from '../data/services/effective-org.service';
import { initializeApp } from './initialize-app';
import { MemoizingAuthorizationService } from './memoizing-authorization.service';
import { BehaviorSubject } from 'rxjs';
import { ampli } from '../ampli';

import { ArraysModule } from '../arrays/arrays.module';
import { ExportModule } from '../export/export.module';
import { GfbModule } from '../gfb/gfb.module';
import { PageModule } from '../page/page.module';
import { SafeModeModule } from '../safemode/safemode.module';
import { CssUrlStyleManager } from '../services/css-url-style-manager.service';
import { googleMapsPromiseFactory } from '../services/google-maps-promise.factory';
import { LazyLoadingScriptService } from '../services/lazy-load-scripts.service';
import { ModalHeightFixerService } from '../services/modal-height-fixer.service';
import { SupportModule } from '../support/support.module';
import { TagsModule } from '../tags/tags.module';
import { UIModule } from '../ui/ui.module';
import { AngularticsConfigService } from './angulartics-config.service';
import { configureModules } from './app-config';
import { DatadogLoggingConfigService } from './datadog-logging-config.service';
import { HttpCacheInterceptor } from './http-cache.interceptor';
import { RequestPacingInterceptor } from './request-pacing.interceptor';
import { HttpContentTypeInterceptor } from './http-content-type.interceptor';
import { HttpRetryInterceptor } from './http-retry.interceptor';
import { HttpUndefinedParamsInterceptor } from './http-undefined-params.interceptor';
import { DataIntelligenceMockInterceptor } from '../data-intelligence/mock/data-intelligence-mock.interceptor';
import { APPCUES, GOOGLE_MAPS, MARKER_CLUSTERER, WINDOW } from './injection-tokens';
import { RootAppRoutesModule } from './root-app-routes.module';
import { UserChangedDetectorService } from './user-changed-detector.service';

import { OrgSwitcherService } from '../page/page-wrapper/org-switcher/org-switcher.service';
import { PureRedux } from '../redux/pure-redux.service';
import { AuthenticationService } from '../services/authentication.service';
import { AppcuesService } from './appcues.service';
import { AppRootComponent } from './app-root.component';
import { CfwdHeaderInterceptor } from './cfwd-header.interceptor';
import { DatadogRumConfigService } from './datadog-rum-config.service';
import { GlobalErrorHandler } from './global-error-handler';
import { OrgHeadersInterceptor } from './org-headers.interceptor';
import { UserChangedDetectorInterceptor } from './user-changed-detector.interceptor';
import Appcues from '@appcues/web-sdk';

@NgModule({
    declarations: [AppRootComponent],
    bootstrap: [AppRootComponent],
    imports: [
        AuthorizerModule.forRoot({
            defaultResourceProvider: DefaultResourceService,
        }),
        Angulartics2RouterlessModule.forRoot(),
        RootAppRoutesModule,

        // Dependencies
        BrowserModule,
        BrowserAnimationsModule,
        PageModule,
        ExportModule,
        GfbModule,
        NgbModule,

        // Non-lazy loaded feature modules (lazy loaded features loaded by the router, see routes.config.ts)
        ArraysModule,
        SupportModule,
        TagsModule,
        UIModule,
        SafeModeModule,
        Pure1UIPlatformModule,
    ],
    providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        // Outermost, so a mocked response skips caching, retries, pacing and org headers
        { provide: HTTP_INTERCEPTORS, useClass: DataIntelligenceMockInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpUndefinedParamsInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpContentTypeInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpCacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpRetryInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RequestPacingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CfwdHeaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: OrgHeadersInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UserChangedDetectorInterceptor, multi: true },
        { provide: WINDOW, useValue: window },
        { provide: GOOGLE_MAPS, useFactory: googleMapsPromiseFactory, deps: [LazyLoadingScriptService] },
        { provide: MARKER_CLUSTERER, useValue: MarkerClusterer },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [
                PureRedux,
                OrgSwitcherService,
                EffectiveOrgService,
                FeatureFlagDxpService,
                PlatformClientService,
                CachedCurrentUserService,
                AuthenticationService,
                AngularticsConfigService,
                DatadogLoggingConfigService,
                DatadogRumConfigService,
                CssUrlStyleManager,
                AppcuesService,
                UserChangedDetectorService,
                DOCUMENT,
            ],
            multi: true,
        },
        { provide: SVG_ROOT, useValue: 'images/' },
        { provide: HIVE_THEMING, useValue: new BehaviorSubject<Themes>(Themes.UINext) },
        { provide: APPCUES, useValue: Appcues },
        importProvidersFrom(
            Pure1UIPlatformModule.forRoot({
                amplitudeTrackCallback: (event, options) => {
                    ampli.track(event, options);
                },
            }),
        ),
        importProvidersFrom(
            NotificationCenterModule.forRoot({
                amplitudeTrackCallback: (event, options) => {
                    ampli.track(event, options);
                },
            }),
        ),
        provideHttpClient(withInterceptorsFromDi()),
        // Overrides the AuthorizerModule.forRoot() provider; see the class for why. It must stay after every
        // importProvidersFrom() above: NotificationCenterModule.forRoot() provides the library's
        // AuthorizationService again, and for one token the last provider in this array wins.
        { provide: AuthorizationService, useClass: MemoizingAuthorizationService },
    ],
})
export class AppModule {
    constructor(
        private readonly modalHeightFixer: ModalHeightFixerService,
        private readonly ngbConfig: NgbConfig,
        private readonly ngbTooltipConfig: NgbTooltipConfig,
        private readonly ngbModalConfig: NgbModalConfig,
        private readonly ngbCarouselConfig: NgbCarouselConfig,
    ) {
        // AppModule constructor runs BEFORE APP_INITIALIZERs — do not start anything
        // that issues HTTP here. Initialize HTTP-dependent services from initializeApp().
        this.modalHeightFixer.run();
        this.configureNgBootstrap();
        configureModules();
    }

    /**
     * Sets global default configuration for NgBootstrap components.
     */
    private configureNgBootstrap(): void {
        this.ngbTooltipConfig.openDelay = 200;

        // Selectively enable animations. Not all animations will work due to our combination of incorrect Bootstrap version
        // and the hacky styles we've thrown on top of them. Ideally we'd fix these all at some point. Realistically... we'll just not animate most stuff. :)
        this.ngbConfig.animation = false; // Disable by default
        this.ngbModalConfig.animation = true; // Modals won't show correctly without animations
        this.ngbModalConfig.modalDialogClass = 'hive-root hive-theme-raspberry';
        this.ngbTooltipConfig.tooltipClass = 'hive-root hive-theme-raspberry';
        this.ngbCarouselConfig.animation = false; // Carousel animations do not work - it just adds a delay without visually animating, which makes it look broken.
    }
}
